import { Link } from '../router/Link';
import type { ChannelRow } from './model';
import styles from './listing.module.css';
import { useCopy } from '../strings/useCopy';

/**
 * A channel in a results grid. Renders props (R13).
 *
 * Two anchors, arranged the way `ListingCard` arranges its three and for the same reason — an anchor
 * inside an anchor is invalid HTML that browsers resolve by dropping one — so the avatar's copy is
 * `aria-hidden` + `tabIndex={-1}` and the name is the one real link. One tab stop per card.
 *
 * The avatar is drawn as a **circle with no fallback image**: an `authorThumbnails[]` is
 * protocol-relative on roughly two thirds of live rows (normalised at the parse boundary) and
 * intermittently absent altogether, so an empty ring is a state this card has to render rather than an
 * error. The initial in it is not decoration — it is what keeps the row scannable when the picture
 * does not arrive.
 */
export interface ChannelCardProps {
  readonly item: ChannelRow;
  readonly tabIndex: number;
  readonly onFocus?: () => void;
}

export function ChannelCard({ item, tabIndex, onFocus }: ChannelCardProps) {
  const words = useCopy().text.listing;
  return (
    <li className={styles.channelCard} onFocus={onFocus}>
      <Link className={styles.avatarLink} href={item.href} tabIndex={-1} ariaHidden>
        <span className={styles.avatar}>
          {item.avatarUrl === null ? (
            <span className={styles.avatarBlank} aria-hidden="true">
              {item.name.slice(0, 1)}
            </span>
          ) : (
            <img className={styles.avatarImage} src={item.avatarUrl} alt="" loading="lazy" decoding="async" />
          )}
        </span>
      </Link>

      <div className={styles.channelText}>
        {/* A row of its own: `.channelText` is a column, so a bare sibling put the tick on the line
            below the name. */}
        <div className={styles.channelName}>
          <Link className={styles.title} href={item.href} title={item.name} tabIndex={tabIndex}>
            {item.name}
          </Link>
          {item.verified ? (
            <span className={styles.verified} title={words.verified}>
              ✓
            </span>
          ) : null}
        </div>

        <div className={styles.meta}>
          {item.handle === null ? null : <span className={styles.handle}>{item.handle}</span>}
          {item.subscriberText === null ? null : (
            <span title={item.subscriberTitle ?? undefined}>
              {words.meta.subscribers(item.subscriberText)}
            </span>
          )}
          {/*
           * No video count. The field is there and it read `0` on 20 of 20 live rows — 2b's rule that
           * the reading is per response, applied to one field (`listing/model.ts`).
           */}
          {item.autoGenerated ? (
            <span
              className={styles.autoGenerated}
              title={words.autoGeneratedTitle}
            >
              {words.autoGenerated}
            </span>
          ) : null}
        </div>

        {item.description === '' ? null : <p className={styles.channelBlurb}>{item.description}</p>}
      </div>
    </li>
  );
}
